import { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Braces, Check, Database, Eye, Pencil, X } from 'lucide-react';
import remarkGfm from 'remark-gfm';

import {
  classifyTemplateTokens,
  type FieldInfo,
  type TemplateToken,
} from '../helpers/context';

import { TemplateContextFieldGroups } from './ContextData';

interface Props {
  value: string;
  onChange: (value: string) => void;
  fields: FieldInfo[];
  /** Extra valid fields (e.g. an LLM step's own output fields). */
  extra?: FieldInfo[];
  placeholder?: string;
  /** Kept for compatibility; editing always happens in the multiline dialog. */
  multiline?: boolean;
  rows?: number;
  /** A clear, task-specific dialog title supplied by the surrounding field. */
  editorTitle?: string;
  /** Show the template and its data references without allowing changes. */
  readOnly?: boolean;
  /** Enable Markdown authoring and preview for user-facing messages. */
  supportsMarkdown?: boolean;
}

/**
 * Compact template field with a dedicated editor. Keeping the inspector to one
 * line makes the step form scannable; the dialog provides room to write, see
 * context, and insert variables without a ghost-text overlay.
 */
export function TemplateInput({
  value,
  onChange,
  fields,
  extra,
  placeholder,
  rows = 3,
  editorTitle = 'Edit text',
  readOnly = false,
  supportsMarkdown = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<'markdown' | 'preview'>(
    readOnly ? 'preview' : 'markdown',
  );
  const [draft, setDraft] = useState(value);
  const editorRef = useRef<HTMLTextAreaElement | null>(null);
  const selectionRef = useRef({ start: value.length, end: value.length });

  const summary = value.replaceAll(/\s+/g, ' ').trim();
  const tokens = useMemo(
    () => classifyTemplateTokens(draft, fields, extra),
    [draft, fields, extra],
  );
  const insertableFields = useMemo<FieldInfo[]>(() => {
    const knownNames = new Set(fields.map((field) => field.name));
    const ownOutputs = new Map(
      (extra ?? []).map((field) => [
        field.name,
        {
          ...field,
          producedBy: 'this step',
          producedByLabel: 'This step’s output',
          producedByKind: undefined,
        } satisfies FieldInfo,
      ]),
    );
    return [
      ...fields.map((field) => ownOutputs.get(field.name) ?? field),
      ...[...ownOutputs.values()].filter(
        (field) => !knownNames.has(field.name),
      ),
    ];
  }, [extra, fields]);
  const usedPaths = useMemo(
    () =>
      new Set(
        tokens
          .flatMap((token) =>
            token.ref && token.known ? [tokenCanonicalPath(token)] : [],
          )
          .filter(Boolean),
      ),
    [tokens],
  );
  const invalidTokens = useMemo(
    () => [
      ...new Map(
        tokens
          .filter((token) => token.ref && !token.known)
          .map((token) => [token.ref!, token]),
      ).values(),
    ],
    [tokens],
  );

  const openEditor = () => {
    setDraft(value);
    setEditorMode(readOnly ? 'preview' : 'markdown');
    selectionRef.current = { start: value.length, end: value.length };
    setOpen(true);
  };

  useEffect(() => {
    if (!open || readOnly) {
      return;
    }
    const frame = requestAnimationFrame(() => {
      const editor = editorRef.current;
      if (!editor) {
        return;
      }
      const start = Math.min(selectionRef.current.start, editor.value.length);
      const end = Math.min(selectionRef.current.end, editor.value.length);
      editor.focus();
      editor.setSelectionRange(start, end);
    });
    return () => cancelAnimationFrame(frame);
  }, [open, readOnly]);

  const rememberSelection = (
    target: HTMLInputElement | HTMLTextAreaElement,
  ) => {
    selectionRef.current = {
      start: target.selectionStart ?? target.value.length,
      end: target.selectionEnd ?? target.value.length,
    };
  };

  const insertField = (name: string) => {
    if (readOnly) {
      return;
    }
    const insert = `{{${name}}}`;
    const editor = editorRef.current;
    const start = editor?.selectionStart ?? selectionRef.current.start;
    const end = editor?.selectionEnd ?? selectionRef.current.end;
    const nextValue = `${draft.slice(0, start)}${insert}${draft.slice(end)}`;
    const nextPosition = start + insert.length;
    selectionRef.current = { start: nextPosition, end: nextPosition };
    setDraft(nextValue);
    requestAnimationFrame(() => {
      const nextEditor = editorRef.current;
      if (!nextEditor) {
        return;
      }
      nextEditor.focus();
      nextEditor.setSelectionRange(nextPosition, nextPosition);
    });
  };

  const save = () => {
    if (readOnly) {
      return;
    }
    onChange(draft);
    setOpen(false);
  };

  return (
    <>
      <TextField
        fullWidth
        value={summary}
        placeholder={placeholder ?? 'Click to write a message…'}
        onClick={openEditor}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openEditor();
          }
        }}
        slotProps={{
          htmlInput: {
            readOnly: true,
            'aria-label': `${editorTitle}; ${readOnly ? 'view' : 'edit'}`,
          },
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label={`${readOnly ? 'View' : 'Edit'} ${editorTitle}`}
                  onClick={openEditor}
                >
                  {readOnly ? <Eye size={15} /> : <Pencil size={15} />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        sx={{
          cursor: 'pointer',
          '& .MuiInputBase-input': {
            cursor: 'pointer',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
        }}
      />

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        fullScreen={supportsMarkdown}
        maxWidth={supportsMarkdown ? false : 'md'}
        aria-labelledby="template-editor-title"
      >
        <DialogTitle
          id="template-editor-title"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}
        >
          <Braces size={18} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {editorTitle}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setOpen(false)}
            aria-label="Close dialog"
            size="small"
          >
            <X size={17} />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {supportsMarkdown && (
            <Box
              sx={{
                p: 1.25,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'background.default',
                flexShrink: 0,
              }}
            >
              <MarkdownHelp />
            </Box>
          )}

          <Box
            sx={{
              flexGrow: 1,
              minHeight: 0,
              display: 'grid',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                md: 'minmax(0, 1fr) minmax(280px, 340px)',
              },
              gridTemplateRows: supportsMarkdown
                ? {
                    xs: 'minmax(0, 1fr) auto',
                    md: 'minmax(0, 1fr)',
                  }
                : undefined,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                minWidth: 0,
                minHeight: 0,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {supportsMarkdown && (
                <Tabs
                  value={editorMode}
                  onChange={(_, nextMode: 'markdown' | 'preview') =>
                    setEditorMode(nextMode)
                  }
                  aria-label="Template editor view"
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    flexShrink: 0,
                  }}
                >
                  <Tab
                    value="markdown"
                    label="Markdown"
                    icon={<Pencil size={15} />}
                    iconPosition="start"
                  />
                  <Tab
                    value="preview"
                    label="Preview"
                    icon={<Eye size={15} />}
                    iconPosition="start"
                  />
                </Tabs>
              )}

              <Box
                sx={{
                  flexGrow: 1,
                  minHeight: 0,
                  pt: supportsMarkdown ? 2 : 0,
                  display: 'flex',
                  overflow: 'hidden',
                }}
              >
                {!supportsMarkdown || editorMode === 'markdown' ? (
                  <TextField
                    fullWidth
                    multiline
                    value={draft}
                    inputRef={editorRef}
                    placeholder={placeholder ?? 'Write your message…'}
                    minRows={Math.max(rows, 8)}
                    autoFocus={!readOnly}
                    onChange={(event) => {
                      setDraft(event.target.value);
                      rememberSelection(event.target);
                    }}
                    onSelect={(event) => {
                      const target = event.target;
                      if (
                        target instanceof HTMLTextAreaElement ||
                        target instanceof HTMLInputElement
                      ) {
                        rememberSelection(target);
                      }
                    }}
                    onClick={(event) => {
                      const target = event.target;
                      if (
                        target instanceof HTMLTextAreaElement ||
                        target instanceof HTMLInputElement
                      ) {
                        rememberSelection(target);
                      }
                    }}
                    slotProps={{
                      htmlInput: {
                        readOnly,
                        'aria-label': 'Template text editor',
                      },
                    }}
                    sx={{
                      height: '100%',
                      '& .MuiInputBase-root': {
                        height: '100%',
                        alignItems: 'flex-start',
                      },
                      '&.MuiInputBase-multiline > .MuiInputBase-input': {
                        height: '100% !important',
                        overflow: 'auto !important',
                        boxSizing: 'border-box',
                        fontFamily: 'inherit',
                        lineHeight: 1.55,
                      },
                    }}
                  />
                ) : (
                  <MarkdownPreview value={draft} />
                )}
              </Box>
            </Box>

            <Box
              sx={{
                minWidth: 0,
                minHeight: 0,
                p: 2,
                borderTop: { xs: 1, md: 0 },
                borderLeft: { xs: 0, md: 1 },
                borderColor: 'divider',
                bgcolor: 'background.default',
                overflow: 'auto',
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  alignItems: 'center',
                  mb: 0.75,
                }}
              >
                <Database size={14} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Available context
                </Typography>
              </Stack>
              <TemplateContextFieldGroups
                fields={insertableFields}
                usedPaths={usedPaths}
                onPathClick={readOnly ? undefined : (path) => insertField(path)}
                maxHeight={supportsMarkdown ? undefined : 240}
              />
              {invalidTokens.length > 0 && (
                <Box sx={{ mt: 0.75 }}>
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: 'block' }}
                  >
                    Check these data references:
                  </Typography>
                  {invalidTokens.slice(0, 3).map((token) => (
                    <Typography
                      key={token.ref}
                      variant="caption"
                      color="error"
                      sx={{ display: 'block', pl: 0.75 }}
                    >
                      {`{{${token.ref}}}`} — {tokenErrorMessage(token)}
                    </Typography>
                  ))}
                  {invalidTokens.length > 3 && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ display: 'block', pl: 0.75 }}
                    >
                      And {invalidTokens.length - 3} more.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5 }}>
          {readOnly ? (
            <Button onClick={() => setOpen(false)}>Close</Button>
          ) : (
            <>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                startIcon={<Check size={15} />}
                onClick={save}
              >
                Apply text
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

function MarkdownHelp() {
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Markdown formatting
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
        }}
      >
        Use the patterns below in your message. You can also insert workflow
        data such as <code>{'{{emailFrom}}'}</code> from the context list.
      </Typography>
      <Stack
        direction="row"
        sx={{
          flexWrap: 'wrap',
          gap: 0.75,
          mt: 0.75,
        }}
      >
        <MarkdownExample source="**bold**" result="bold" />
        <MarkdownExample source="*italic*" result="italic" />
        <MarkdownExample source="- item" result="list item" />
        <MarkdownExample source="1. item" result="numbered item" />
      </Stack>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          display: 'block',
          mt: 0.5,
        }}
      >
        Leave a blank line before a list when it follows regular text.
      </Typography>
    </Box>
  );
}

function MarkdownExample({
  source,
  result,
}: {
  source: string;
  result: string;
}) {
  return (
    <Box
      sx={{
        px: 0.75,
        py: 0.35,
        border: 1,
        borderColor: 'divider',
        borderRadius: 0.75,
        bgcolor: 'background.paper',
        fontSize: '0.75rem',
      }}
    >
      <Box component="code" sx={{ fontFamily: 'monospace' }}>
        {source}
      </Box>{' '}
      → {result}
    </Box>
  );
}

function MarkdownPreview({ value }: { value: string }) {
  if (!value.trim()) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          color: 'text.secondary',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
        }}
      >
        <Typography variant="body2">
          Nothing to preview yet. Switch to Markdown to start writing.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        minWidth: 0,
        overflow: 'auto',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        typography: 'body1',
        lineHeight: 1.55,
        '& > p': { mt: 0, mb: 1.5 },
        '& > p:last-child': { mb: 0 },
        '& > ul, & > ol': { mt: 0, mb: 1.5, pl: 3 },
        '& li': { mb: 0.5 },
        '& li:last-child': { mb: 0 },
        '& strong': { fontWeight: 700 },
        '& em': { fontStyle: 'italic' },
      }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
    </Box>
  );
}

function tokenCanonicalPath(token: TemplateToken): string {
  // A known reference is guaranteed by `resolveTemplatePath` to have a
  // canonical bracket-index form. Keeping `ref` as a guard makes this UI
  // tolerant of incomplete data while a workflow is being edited.
  return token.canonicalPath ?? token.ref ?? '';
}

function tokenErrorMessage(token: TemplateToken): string {
  return token.reason ?? 'This data is not available at this step.';
}
